import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  ListGroup,
  Button,
  Card,
  Form,
  Accordion,
} from 'react-bootstrap';
import {
  FaShoppingCart,
  FaMoneyBill,
  FaBox,
  FaCashRegister,
  FaUsers,
  FaStickyNote,
  FaTrash,
  FaFileExcel,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import Message from '../components/Message';
import { addToCart, removeFromCart } from '../slices/cartSlice';
import { useGetProductsQuery } from '../slices/productsApiSlice';

const CartForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);

  const { data } = useGetProductsQuery({ pageNumber: 1 });
  const productList = data?.products || [];

  // -------------------------------
  // Excel Upload
  // -------------------------------
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || productList.length === 0) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      rows.forEach((row) => {
        const name = row.name?.trim();
        const qty = parseInt(row.qty, 10);

        if (!name || isNaN(qty)) return;

        const matchedProduct = productList.find(
          (p) => p.name.trim().toLowerCase() === name.toLowerCase()
        );

        if (matchedProduct) {
          dispatch(
            addToCart({
              _id: matchedProduct._id,
              name: matchedProduct.name,
              price: matchedProduct.price,
              qty,
              countInStock: matchedProduct.countInStock,
              images: matchedProduct.images || [],
            })
          );
        }
      });
    };
    reader.readAsArrayBuffer(file);
  };

  // -------------------------------
  // Export Cart to Excel
  // -------------------------------
  const exportCartToExcel = () => {
    const data = cartItems.map((item) => ({
      _id: item._id,
      name: item.name,
      price: item.price,
      qty: item.qty,
      total: item.price * item.qty,
      countInStock: item.countInStock,
      image: item.images?.[0] || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'CartItems');
    XLSX.writeFile(workbook, 'CartItems.xlsx');
  };

  // -------------------------------
  // Navigation Categories
  // -------------------------------
  const categories = {
    'Browse Forms': {
      color: '#007bff',
      icon: <FaShoppingCart className="me-2" />,
      items: {
        salesform: 'Proceed To SalesForm',
        supplysform: 'Proceed To SupplyForm',
        transfersform: 'Proceed To TransferForm',
        stocksform: 'Proceed To StockForm',
        openingstocksform: 'Proceed To Op-StockForm',
      },
    },

    'Income & Expense': {
      color: '#ffc107',
      icon: <FaMoneyBill className="me-2" />,
      items: {
        expenselist: 'Income / Expenses',
      },
    },

    'Products & Inventory': {
      color: '#28a745',
      icon: <FaBox className="me-2" />,
      items: {
        productlist: 'Itemlist',
        staffhomescreen: 'Stock Balance',
      },
    },

    Billing: {
      color: '#6f42c1',
      icon: <FaStickyNote className="me-2" />,
      items: {
        billsform: 'Proceed To FinalBillForm',
        staffbill: 'Final Insu Bill',
      },
    },

    'Cash & Bank': {
      color: '#17a2b8',
      icon: <FaCashRegister className="me-2" />,
      items: {
        cashlist: 'Cash Collected',
        bankuserlist: 'Bank',
      },
    },

    'Customers & Suppliers': {
      color: '#fd7e14',
      icon: <FaUsers className="me-2" />,
      items: {
        customeruserlist: 'Customerlist',
        supplierlist: 'Supplierlist',
      },
    },
  };

  const navHandler = (key) => {
    navigate(`/login?redirect=/${key}`);
  };

  // -------------------------------
  // PAGE SEARCH
  // -------------------------------
  const [search, setSearch] = useState('');

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;

    const lower = search.toLowerCase();
    const result = {};

    Object.entries(categories).forEach(([catName, catData]) => {
      const matchedItems = Object.entries(catData.items).filter(([path, label]) =>
        label.toLowerCase().includes(lower)
      );

      if (matchedItems.length > 0) {
        result[catName] = {
          ...catData,
          items: Object.fromEntries(matchedItems),
        };
      }
    });

    return result;
  }, [search]);

  // -------------------------------
  // PRODUCT SEARCH (LEFT PANEL)
  // -------------------------------
  const [productSearch, setProductSearch] = useState('');

  const filteredCartItems = useMemo(() => {
    if (!productSearch.trim()) return cartItems;

    return cartItems.filter((item) =>
      item.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      String(item.price).includes(productSearch) ||
      String(item.qty).includes(productSearch) ||
      String(item._id).includes(productSearch)
    );
  }, [productSearch, cartItems]);

  return (
    <Row>
      {/* LEFT PANEL */}
      <Col md={8}>
        <h1 className="mb-4">Browse Pages</h1>

        {/* ⭐ PRODUCT SEARCH BOX */}
        <Form.Group className="mb-3">
          <Form.Label>Search Products</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search product name, price, qty, ID..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="excelUpload" className="mb-3">
          <Form.Label>Upload Excel File</Form.Label>
          <Form.Control type="file" accept=".xlsx, .xls" onChange={handleExcelUpload} />
        </Form.Group>

        {filteredCartItems.length === 0 ? (
          <Message>No products match your search.</Message>
        ) : (
          <ListGroup variant="flush">
            {filteredCartItems.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row className="align-items-center">
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Thumbnail"
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                      />
                    )}
                  </Col>

                  <Col md={3}>
                    <Link to={`/product/${item._id}`}>{item.name}</Link>
                  </Col>

                  <Col md={2}>${item.price}</Col>

                  <Col md={2}>
                    <Button
                      variant="light"
                      onClick={() =>
                        dispatch(addToCart({ ...item, qty: item.qty - 1 }))
                      }
                      disabled={item.qty === 1}
                    >
                      -
                    </Button>
                    <span className="mx-2">{item.qty}</span>
                    <Button
                      variant="light"
                      onClick={() =>
                        dispatch(addToCart({ ...item, qty: item.qty + 1 }))
                      }
                      disabled={item.qty === item.countInStock}
                    >
                      +
                    </Button>
                  </Col>

                  <Col md={2}>${(item.price * item.qty).toFixed(2)}</Col>

                  <Col md={1}>
                    <Button variant="light" onClick={() => dispatch(removeFromCart(item._id))}>
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      {/* RIGHT PANEL */}
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>
                Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)}) items
              </h2>
              ${cartItems.reduce((acc, item) => acc + item.qty * item.price, 0).toFixed(2)}
            </ListGroup.Item>

            {/* SEARCH BAR */}
            <ListGroup.Item>
              <Form.Control
                type="text"
                placeholder="Search pages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </ListGroup.Item>

            {/* COLLAPSIBLE SECTIONS */}
            <ListGroup.Item>
              <Accordion alwaysOpen>
                {Object.entries(filteredCategories).map(
                  ([categoryName, { color, icon, items }], idx) => (
                    <Accordion.Item eventKey={idx.toString()} key={categoryName}>
                      <Accordion.Header>
                        <span style={{ color }}>{icon}{categoryName}</span>
                      </Accordion.Header>

                      <Accordion.Body>
                        <Row>
                          {Object.entries(items).map(([path, label]) => (
                            <Col md={6} className="mb-2" key={path}>
                              <Button
                                className="w-100"
                                style={{ backgroundColor: color, borderColor: color }}
                                onClick={() => navHandler(path)}
                              >
                                {label}
                              </Button>
                            </Col>
                          ))}
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  )
                )}
              </Accordion>
            </ListGroup.Item>

            {/* EXPORT BUTTON */}
            {cartItems.length > 0 && (
              <ListGroup.Item>
                <Button className="w-100" onClick={exportCartToExcel}>
                  <FaFileExcel className="me-2" />
                  Download Cart to Excel
                </Button>
              </ListGroup.Item>
            )}
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default CartForm;
